import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    ArrowRight,
    BriefcaseBusiness,
    Building2,
    CalendarDays,
    CheckCircle2,
    FileUp,
    MapPin,
    Search,
    Send,
    X,
} from "lucide-react";
import { applyForCareer } from "../../api/action/career.jsx";
import {
    careerApplyHref,
    formatCareerDate,
    getCareerDescription,
    getEmploymentLabel,
    getWorkModeLabel,
    useCareerOpenings,
} from "../../hooks/useCareerOpenings.jsx";
import { academyLogger } from "utils/logger";

const initialForm = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    currentLocation: "",
    linkedInUrl: "",
    portfolioUrl: "",
    noticePeriod: "",
    expectedCompensation: "",
    whyWorkWithUs: "",
};

const alertStyles = {
    success: "border-emerald-200 bg-emerald-50 text-emerald-800",
    error: "border-red-200 bg-red-50 text-red-800",
    warning: "border-amber-200 bg-amber-50 text-amber-800",
    info: "border-sky-200 bg-sky-50 text-sky-800",
};

const fieldClass = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#123a68] focus:ring-2 focus:ring-[#123a68]/15 disabled:cursor-not-allowed disabled:bg-slate-100";
const labelClass = "mb-1.5 block text-sm font-bold text-slate-700";
const primaryButtonClass = "inline-flex items-center justify-center gap-2 rounded-lg bg-[#123a68] px-4 py-2.5 text-sm font-extrabold text-white shadow-sm transition hover:bg-[#0f2d4f] disabled:cursor-not-allowed disabled:opacity-70";
const secondaryButtonClass = "inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-extrabold text-slate-900 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70";

const getCompanyName = (company = {}) => (
    company.name
    || company.companyName
    || company.businessName
    || "Our Team"
);

const getCompanyLogo = (company = {}) => (
    company.logoImageContent?.displayLink
    || company.logoLocation
    || ""
);

const normalizeList = (value) => {
    if (Array.isArray(value)) {
        return value.filter(Boolean);
    }

    if (typeof value === "string") {
        return value
            .split(/\r?\n|,/)
            .map((item) => item.trim())
            .filter(Boolean);
    }

    return [];
};

const getSalaryLabel = (career = {}) => {
    const min = career.salaryMin || career.minimumSalary;
    const max = career.salaryMax || career.maximumSalary;
    const currency = career.salaryCurrency || "USD";

    if (career.salaryRange) {
        return career.salaryRange;
    }

    if (min && max) {
        return `${currency} ${min} - ${max}`;
    }

    if (min) {
        return `${currency} ${min}+`;
    }

    return "";
};

const LoadingDot = ({ className = "" }) => (
    <span
        className={`inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
        aria-hidden="true"
    />
);

const Field = ({
    id,
    label,
    name,
    value,
    onChange,
    type = "text",
    required = false,
    autoComplete,
    placeholder,
}) => (
    <label htmlFor={id || name} className="block">
        <span className={labelClass}>{label}</span>
        <input
            id={id || name}
            name={name}
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            autoComplete={autoComplete}
            placeholder={placeholder}
            className={fieldClass}
        />
    </label>
);

const Career = () => {
    const company = useSelector((state) => state.companyReducer?.company || state.company?.companyDetails || {});
    const companyId = company?.primaryId;
    const companyName = getCompanyName(company);
    const logoUrl = getCompanyLogo(company);

    const { careers, loading, error } = useCareerOpenings(companyId);
    const [form, setForm] = useState(initialForm);
    const [resumeFile, setResumeFile] = useState(null);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [selectedCareerId, setSelectedCareerId] = useState("");
    const [applicationDialogOpen, setApplicationDialogOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [workModeFilter, setWorkModeFilter] = useState("ALL");
    const [submitting, setSubmitting] = useState(false);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "info" });

    useEffect(() => {
        if (!selectedCareerId && careers.length > 0) {
            setSelectedCareerId(String(careers[0].primaryId));
        }
    }, [careers, selectedCareerId]);

    const selectedCareer = useMemo(
        () => careers.find((career) => String(career.primaryId) === String(selectedCareerId)) || null,
        [careers, selectedCareerId]
    );

    const workModeOptions = useMemo(() => (
        Array.from(new Set(careers.map((career) => career.workMode).filter(Boolean)))
    ), [careers]);

    const filteredCareers = useMemo(() => {
        const query = searchText.trim().toLowerCase();

        return careers.filter((career) => {
            const searchable = [
                career.title,
                career.primaryId,
                career.department,
                career.location,
                getCareerDescription(career),
                ...normalizeList(career.skills),
            ].filter(Boolean).join(" ").toLowerCase();

            const matchesSearch = !query || searchable.includes(query);
            const matchesWorkMode = workModeFilter === "ALL" || career.workMode === workModeFilter;

            return matchesSearch && matchesWorkMode;
        });
    }, [careers, searchText, workModeFilter]);

    const departmentsCount = useMemo(() => (
        new Set(careers.map((career) => career.department).filter(Boolean)).size
    ), [careers]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((current) => ({ ...current, [name]: value }));
    };

    const closeAlert = () => setAlert({ open: false, message: "", severity: "info" });

    const openApplicationDialog = (career) => {
        if (career?.primaryId) {
            setSelectedCareerId(String(career.primaryId));
        }
        closeAlert();
        setApplicationDialogOpen(true);
    };

    const closeApplicationDialog = () => {
        if (!submitting) {
            setApplicationDialogOpen(false);
        }
    };

    const handleApply = (career) => {
        openApplicationDialog(career);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!companyId || !selectedCareerId) {
            setAlert({
                open: true,
                message: "Please choose a role before submitting.",
                severity: "warning",
            });
            return;
        }

        if (!resumeFile) {
            setAlert({
                open: true,
                message: "Please upload your resume before submitting.",
                severity: "warning",
            });
            return;
        }

        setSubmitting(true);
        closeAlert();

        try {
            await applyForCareer({
                companyId,
                careerId: selectedCareerId,
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                email: form.email.trim(),
                phoneNumber: form.phoneNumber,
                currentLocation: form.currentLocation.trim(),
                linkedInUrl: form.linkedInUrl.trim(),
                portfolioUrl: form.portfolioUrl.trim(),
                noticePeriod: form.noticePeriod.trim(),
                expectedCompensation: form.expectedCompensation.trim(),
                whyWorkWithUs: form.whyWorkWithUs.trim(),
            }, resumeFile);

            setForm(initialForm);
            setResumeFile(null);
            setFileInputKey((current) => current + 1);
            setApplicationDialogOpen(false);
            setAlert({
                open: true,
                message: "Applied successfully.",
                severity: "success",
            });
        } catch (err) {
            academyLogger.error("Applicant submission failed", err);
            setAlert({
                open: true,
                message: "We could not submit your application. Please try again.",
                severity: "error",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const renderApplicationForm = () => (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label htmlFor="career-opening" className="block sm:col-span-2">
                <span className={labelClass}>Opening</span>
                <select
                    id="career-opening"
                    value={selectedCareerId}
                    onChange={(event) => setSelectedCareerId(event.target.value)}
                    className={fieldClass}
                    required
                >
                    {careers.map((career) => (
                        <option key={career.primaryId} value={String(career.primaryId)}>
                            {career.title}{career.primaryId ? ` (ID ${career.primaryId})` : ""}
                        </option>
                    ))}
                </select>
            </label>

            <Field id="career-firstname-textfield" label="First Name" name="firstName" autoComplete="given-name" value={form.firstName} onChange={handleChange} required />
            <Field id="career-lastname-textfield" label="Last Name" name="lastName" autoComplete="family-name" value={form.lastName} onChange={handleChange} required />
            <Field id="career-phonenumber-input" label="Phone" name="phoneNumber" autoComplete="tel" value={form.phoneNumber} onChange={handleChange} type="tel" placeholder="+1 (555) 123-4567" required />
            <Field id="career-email-textfield" label="Email" name="email" autoComplete="email" value={form.email} onChange={handleChange} type="email" required />
            <Field label="Current Location" name="currentLocation" autoComplete="address-level2" value={form.currentLocation} onChange={handleChange} />
            <Field label="Notice Period" name="noticePeriod" value={form.noticePeriod} onChange={handleChange} placeholder="Immediate, 30 days, 60 days" />
            <Field label="LinkedIn URL" name="linkedInUrl" value={form.linkedInUrl} onChange={handleChange} type="url" />
            <Field label="Portfolio URL" name="portfolioUrl" value={form.portfolioUrl} onChange={handleChange} type="url" />
            <Field label="Expected Compensation" name="expectedCompensation" value={form.expectedCompensation} onChange={handleChange} />

            <label htmlFor="career-resumeurl-customfileinput" className="block rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 sm:col-span-2">
                <span className="mb-2 flex items-center gap-2 text-sm font-extrabold text-slate-800">
                    <FileUp size={18} />
                    Resume {resumeFile ? `- ${resumeFile.name}` : ""}
                </span>
                <input
                    key={fileInputKey}
                    id="career-resumeurl-customfileinput"
                    name="resumeUrl"
                    type="file"
                    onChange={(event) => setResumeFile(event.target.files?.[0] || null)}
                    accept=".pdf,.doc,.docx,.rtf"
                    className="w-full text-sm text-slate-700 file:mr-4 file:rounded-md file:border-0 file:bg-[#123a68] file:px-4 file:py-2 file:text-sm file:font-bold file:text-white hover:file:bg-[#0f2d4f]"
                    required={!resumeFile}
                />
                <span className="mt-2 block text-xs font-semibold text-slate-500">
                    {resumeFile ? "Ready to upload with your application." : "Accepted formats: PDF, DOC, DOCX, RTF"}
                </span>
            </label>

            <label htmlFor="career-workwithus-textfield" className="block sm:col-span-2">
                <span className={labelClass}>Why do you want to work with us?</span>
                <textarea
                    id="career-workwithus-textfield"
                    name="whyWorkWithUs"
                    value={form.whyWorkWithUs}
                    onChange={handleChange}
                    rows={4}
                    className={fieldClass}
                />
            </label>

            <div className="sm:col-span-2">
                <button id="career-submit-button" type="submit" disabled={submitting} className={`${primaryButtonClass} w-full sm:w-auto sm:min-w-[220px]`}>
                    {submitting ? <LoadingDot /> : <FileUp size={18} />}
                    {submitting ? "Submitting" : "Submit Application"}
                    {!submitting && <Send size={18} />}
                </button>
            </div>
        </form>
    );

    const renderRoleCard = (career) => {
        const skills = normalizeList(career.skills).slice(0, 4);
        const applyHref = careerApplyHref(career);
        const useExternalApply = Boolean(career.applicationUrl);

        return (
            <article key={career.primaryId} className="flex h-full flex-col gap-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                    <span className="rounded-full bg-[#e8f2ff] px-3 py-1 text-xs font-extrabold text-[#123a68]">
                        {career.department || "Open Role"}
                    </span>
                    {career.primaryId && <span className="text-xs font-bold text-slate-500">ID {career.primaryId}</span>}
                </div>

                <div>
                    <h3 className="mb-2 text-xl font-extrabold leading-tight text-slate-950">{career.title}</h3>
                    <p className="text-sm leading-6 text-slate-600">
                        {getCareerDescription(career) || "Explore this opening and connect with our team."}
                    </p>
                </div>

                <dl className="grid grid-cols-1 gap-3 text-sm text-slate-600 sm:grid-cols-2">
                    <div className="flex items-center gap-2">
                        <BriefcaseBusiness size={16} />
                        <span>{getEmploymentLabel(career.employmentType)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Building2 size={16} />
                        <span>{getWorkModeLabel(career.workMode)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <MapPin size={16} />
                        <span>{career.location || "Flexible"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <CalendarDays size={16} />
                        <span>Closes {formatCareerDate(career.closingDate)}</span>
                    </div>
                </dl>

                {skills.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {skills.map((skill) => (
                            <span key={skill} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-bold text-slate-700">
                                {skill}
                            </span>
                        ))}
                    </div>
                )}

                <div className="mt-auto flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={() => setSelectedCareerId(String(career.primaryId))}
                        className={secondaryButtonClass}
                    >
                        View Details
                    </button>
                    {useExternalApply ? (
                        <a
                            href={applyHref}
                            target={career.applicationUrl ? "_blank" : undefined}
                            rel="noreferrer"
                            className={primaryButtonClass}
                        >
                            Apply
                            <ArrowRight size={16} />
                        </a>
                    ) : (
                        <button type="button" onClick={() => handleApply(career)} className={primaryButtonClass}>
                            Apply
                            <ArrowRight size={16} />
                        </button>
                    )}
                </div>
            </article>
        );
    };

    return (
        <main className="min-h-screen bg-[#f6f8fb] text-slate-950">
            <section className="border-b border-slate-200 bg-white">
                <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-8 px-4 py-12 sm:px-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:px-8 lg:py-16">
                    <div className="space-y-6">
                        <span className="inline-flex w-fit rounded-full bg-[#e8f2ff] px-3 py-1 text-xs font-extrabold uppercase tracking-[0.08em] text-[#123a68]">
                            Careers
                        </span>
                        <div className="space-y-4">
                            <h1 className="max-w-4xl text-4xl font-black leading-tight text-slate-950 sm:text-5xl lg:text-6xl">
                                Build with {companyName}
                            </h1>
                            <p className="max-w-3xl text-lg leading-8 text-slate-600">
                                Explore published openings, review role details, and submit your application to the team.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3 sm:flex-row">
                            <button
                                type="button"
                                onClick={() => openApplicationDialog(selectedCareer || careers[0])}
                                className={`${primaryButtonClass} w-full sm:w-auto`}
                            >
                                <BriefcaseBusiness size={18} />
                                Submit Application
                            </button>
                            <a href="#open-roles" className={`${secondaryButtonClass} w-full sm:w-auto`}>
                                <Search size={18} />
                                Browse Roles
                            </a>
                        </div>
                    </div>

                    <aside className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                        <div className="space-y-5">
                            {logoUrl ? (
                                <img
                                    src={logoUrl}
                                    alt={`${companyName} logo`}
                                    className="h-24 w-24 rounded-lg border border-slate-200 bg-white object-contain p-2"
                                />
                            ) : (
                                <div className="grid h-24 w-24 place-items-center rounded-lg bg-[#e8f2ff] text-[#123a68]">
                                    <Building2 size={36} />
                                </div>
                            )}
                            <dl className="grid grid-cols-3 gap-4">
                                <div>
                                    <dt className="text-3xl font-black text-slate-950">{careers.length}</dt>
                                    <dd className="text-xs font-bold text-slate-500">Open Roles</dd>
                                </div>
                                <div>
                                    <dt className="text-3xl font-black text-slate-950">{departmentsCount}</dt>
                                    <dd className="text-xs font-bold text-slate-500">Teams</dd>
                                </div>
                                <div>
                                    <dt className="text-3xl font-black text-slate-950">{workModeOptions.length || 0}</dt>
                                    <dd className="text-xs font-bold text-slate-500">Modes</dd>
                                </div>
                            </dl>
                        </div>
                    </aside>
                </div>
            </section>

            <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
                <div id="open-roles" className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
                    <div className="space-y-6">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h2 className="text-3xl font-black text-slate-950">Open Roles</h2>
                                <p className="mt-1 text-sm text-slate-500">Published openings from the hiring team.</p>
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[220px_180px]">
                                <label htmlFor="career-search" className="sr-only">Search roles</label>
                                <input
                                    id="career-search"
                                    type="search"
                                    placeholder="Search roles"
                                    value={searchText}
                                    onChange={(event) => setSearchText(event.target.value)}
                                    className={fieldClass}
                                />
                                <label htmlFor="career-work-mode" className="sr-only">Work mode</label>
                                <select
                                    id="career-work-mode"
                                    value={workModeFilter}
                                    onChange={(event) => setWorkModeFilter(event.target.value)}
                                    className={fieldClass}
                                >
                                    <option value="ALL">All Work Modes</option>
                                    {workModeOptions.map((mode) => (
                                        <option key={mode} value={mode}>{getWorkModeLabel(mode)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {loading && (
                            <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-5 text-slate-600">
                                <LoadingDot className="text-[#123a68]" />
                                <span>Loading current openings...</span>
                            </div>
                        )}

                        {!loading && error && (
                            <div className={`rounded-lg border p-4 text-sm font-semibold ${alertStyles.warning}`}>
                                {error}
                            </div>
                        )}

                        {!loading && !error && filteredCareers.length === 0 && (
                            <div className="rounded-lg border border-slate-200 bg-white p-6">
                                <h3 className="text-lg font-black text-slate-950">No open roles right now</h3>
                                <p className="mt-2 text-sm leading-6 text-slate-500">
                                    You can still submit a general application below and the team will keep it on file.
                                </p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-4">
                            {filteredCareers.map((career) => renderRoleCard(career))}
                        </div>
                    </div>

                    <aside className="h-fit rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:sticky lg:top-6">
                        <div className="space-y-5">
                            <div className="flex items-center gap-3">
                                <CheckCircle2 size={20} color="#123a68" />
                                <h2 className="text-lg font-black text-slate-950">Role Details</h2>
                            </div>

                            {selectedCareer ? (
                                <>
                                    <div>
                                        <h3 className="text-2xl font-black leading-tight text-slate-950">{selectedCareer.title}</h3>
                                        <p className="mt-1 text-sm text-slate-500">
                                            {[selectedCareer.department, selectedCareer.primaryId ? `ID ${selectedCareer.primaryId}` : ""].filter(Boolean).join(" | ")}
                                        </p>
                                    </div>
                                    <hr className="border-slate-200" />
                                    <dl className="space-y-2 text-sm leading-6 text-slate-600">
                                        <div><strong>Location:</strong> {selectedCareer.location || "Flexible"}</div>
                                        <div><strong>Employment:</strong> {getEmploymentLabel(selectedCareer.employmentType)}</div>
                                        <div><strong>Work mode:</strong> {getWorkModeLabel(selectedCareer.workMode)}</div>
                                        <div><strong>Experience:</strong> {selectedCareer.experienceLevel || "Open"}</div>
                                        <div><strong>Closing:</strong> {formatCareerDate(selectedCareer.closingDate)}</div>
                                        {getSalaryLabel(selectedCareer) && <div><strong>Salary:</strong> {getSalaryLabel(selectedCareer)}</div>}
                                    </dl>
                                    {(selectedCareer.description || selectedCareer.requirements || selectedCareer.responsibilities) && <hr className="border-slate-200" />}
                                    {selectedCareer.description && (
                                        <section>
                                            <h4 className="mb-1 text-sm font-black text-slate-950">About The Role</h4>
                                            <p className="whitespace-pre-line text-sm leading-6 text-slate-600">{selectedCareer.description}</p>
                                        </section>
                                    )}
                                    {normalizeList(selectedCareer.requirements).length > 0 && (
                                        <section>
                                            <h4 className="mb-1 text-sm font-black text-slate-950">Requirements</h4>
                                            <p className="whitespace-pre-line text-sm leading-6 text-slate-600">{normalizeList(selectedCareer.requirements).join("\n")}</p>
                                        </section>
                                    )}
                                    <button type="button" onClick={() => handleApply(selectedCareer)} className={`${primaryButtonClass} w-full`}>
                                        Apply For This Role
                                        <ArrowRight size={16} />
                                    </button>
                                </>
                            ) : (
                                <p className="text-sm leading-6 text-slate-500">
                                    Select an opening to review details, or send a general application below.
                                </p>
                            )}
                        </div>
                    </aside>
                </div>
            </section>

            {applicationDialogOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4 py-6" role="presentation" onMouseDown={closeApplicationDialog}>
                    <section
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="career-application-title"
                        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-2xl"
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <header className="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4">
                            <h2 id="career-application-title" className="text-xl font-black text-slate-950">
                                {selectedCareer?.title ? `Apply for ${selectedCareer.title}` : "Submit Application"}
                            </h2>
                            <button
                                type="button"
                                onClick={closeApplicationDialog}
                                disabled={submitting}
                                className="grid h-9 w-9 place-items-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                                aria-label="Close application form"
                            >
                                <X size={18} />
                            </button>
                        </header>
                        <div className="p-5">
                            {renderApplicationForm()}
                        </div>
                    </section>
                </div>
            )}

            {alert.open && (
                <div className="fixed bottom-5 left-1/2 z-50 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2">
                    <div className={`flex items-start justify-between gap-4 rounded-lg border p-4 shadow-lg ${alertStyles[alert.severity] || alertStyles.info}`}>
                        <p className="text-sm font-semibold">{alert.message}</p>
                        <button type="button" onClick={closeAlert} className="text-current opacity-70 transition hover:opacity-100" aria-label="Dismiss alert">
                            <X size={16} />
                        </button>
                    </div>
                </div>
            )}
        </main>
    );
};

export default Career;
